import { ITranslationsCollection } from '@estee/elc-service';

import { BAD_CHAR_VALIDATOR_NAME, fieldContainsBadCharErrorKey } from '~constants/Constants';
import {
    ICheckableItem,
    ICheckboxGroup,
    ICustomValidation,
    IDropDownOptionConfig,
    IFieldsValidations,
    IFieldValidationsConfig,
    IFormSchema,
    IFormSection,
    InputSchemaType,
    isCheckboxGroup,
    IValidatorMessages
} from '~domain/entities/Config';

// This is basically the same thing as defined ELC FormValidator, but accepts custom validator params。
export interface IValidatorObj {
    [key: string]: string | Omit<ICustomValidation, 'validator'>;
}

interface IFieldValidationsOptions extends IFieldValidationsConfig {
    validationConfigs?: (string | ICustomValidation)[];
    errorMessages?: IValidatorMessages;
    translations: ITranslationsCollection;
}

/**
 * A utility to transform field validation configurations to FormValidator compatible validations
 */
export class FieldValidations {
    valueType?: string;
    translations: ITranslationsCollection;
    validations?: IValidatorObj;
    options?: IDropDownOptionConfig[];
    isRequired: boolean = false;

    constructor(options?: IFieldValidationsOptions) {
        const {
            type: valueType,
            validations: validationConfigs,
            options: fieldOptions,
            translations = {},
            errorMessages = {}
        } = options || {};
        this.valueType = valueType;
        this.options = fieldOptions?.map(({ value, label = '', labelKey = '' }) => ({
            value: `${value}`,
            label,
            labelKey
        }));
        this.translations = translations;
        const fieldContainsBadCharError = translations[fieldContainsBadCharErrorKey];
        // This will be passed to ELC Field component, empty object ({}) will cause issue but undefined is fine.
        this.validations = validationConfigs?.reduce((validations, validation) => {
            if (typeof validation === 'string') {
                // Separate to 1) validator name without parameters, e.g. 'required', 'min', and 2) param
                const [validator, param = ''] = validation.split(':');
                if (validator === BAD_CHAR_VALIDATOR_NAME) {
                    return {
                        ...validations,
                        [`pattern:${param}`]: fieldContainsBadCharError
                    };
                } else {
                    return {
                        ...validations,
                        [validation]: this.getValidationErrorMessage(validator, errorMessages)
                    };
                }
            } else {
                const { validator, ...params } = validation as ICustomValidation;

                return {
                    ...validations,
                    [validator]: {
                        ...params,
                        errorMessage: this.getValidationErrorMessage(validator, errorMessages)
                    }
                };
            }
        }, {});

        this.isRequired = !!validationConfigs?.includes('required');
        if (this.isRequired && fieldOptions) {
            // Due to yup doesn't allow multiple Schema on one field, and yup.oneOf Schema doesn't provide any tests,
            // it's not possible to have 'required' and 'oneOf' Schema at the same time.
            delete this.validations?.required;
            this.validations = {
                oneOf: {
                    values: fieldOptions.map((n) => n.value),
                    required: true,
                    errorMessage: this.getValidationErrorMessage('required', errorMessages)
                }
            };
        }
    }

    private getValidationErrorMessage(validator: string, errorMessages: IValidatorMessages) {
        const transKey = (errorMessages[validator] || errorMessages.default) as string | undefined;
        const errorMessage = (transKey?.length && this.translations[transKey]) || transKey || '';

        return errorMessage;
    }

    public get maxLength() {
        if (!this.validations) {
            return undefined;
        }
        const maxValidation = Object.keys(this.validations).find(
            (key) => typeof key === 'string' && key.split(':')[0] === 'max'
        );

        return maxValidation ? parseInt(maxValidation.split(':')[1], 10) : undefined;
    }

    // Update validation's error message by registered validator name - without params
    public setErrorMessage = (validatorName: string, errorMessage: string) => {
        if (!this.validations) {
            return;
        }

        const validator = Object.keys(this.validations).find(
            (n) => n.split(':')[0] === validatorName
        );
        if (validator) {
            const validation = this.validations[validator];
            if (typeof validation === 'object') {
                validation.errorMessage = errorMessage;
            } else {
                this.validations = {
                    ...this.validations,
                    [validator]: errorMessage
                };
            }
        }
    };
}

const setFieldValidatorMessages = (
    validatorMessages: Record<string, IValidatorMessages>,
    item: InputSchemaType
) => {
    if (isCheckboxGroup(item)) {
        (item as ICheckboxGroup).items.forEach((checkbox) => {
            setFieldValidatorMessages(validatorMessages, checkbox as ICheckableItem);
        });
    } else {
        const { name, errorMessages } = item;
        if (errorMessages) {
            validatorMessages[name] = errorMessages;
        }
    }
};
const getValidationMessages = (sections: IFormSection[]) => {
    return sections.reduce((validatorMessages, section) => {
        const messages: Record<string, IValidatorMessages> = {};
        section.items.forEach((item) => setFieldValidatorMessages(messages, item));

        return { ...validatorMessages, ...messages };
    }, {}) as Record<string, IValidatorMessages>;
};

// All sign-up form fields' validations, with messages translated.
// badCharValidation is merged into validation object for string fields
export const composeFieldsValidations = (
    formSchema: IFormSchema,
    validationConfigs: IFieldsValidations,
    translations: ITranslationsCollection,
    badCharValidation?: string
) => {
    const fieldsValidatorMessages = getValidationMessages(formSchema.sections);

    return Object.keys(validationConfigs).reduce((fieldsValidations, fieldName) => {
        const config = validationConfigs[fieldName];
        const { type, validations } = config;
        const fieldValidations = new FieldValidations({
            ...config,
            validations:
                type === 'string' && badCharValidation
                    ? (validations ?? []).concat(`${BAD_CHAR_VALIDATOR_NAME}:${badCharValidation}`)
                    : validations,
            translations,
            errorMessages: fieldsValidatorMessages[fieldName]
        });

        return {
            ...fieldsValidations,
            [fieldName]: fieldValidations
        };
    }, {});
};
